import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import classes from "./EmployeePhotoEditor.module.scss";
import CustomButton from "../CustomButton/CustomButton";
import {
  clampOffsets,
  getInitialCoverScale,
  MAX_ZOOM_PERCENT,
  MIN_ZOOM_PERCENT,
  renderCroppedPhoto,
} from "../../../utils/Admin/employeePhotoCrop";
import { loadEditableImage } from "../../../utils/Admin/loadEditableImage";
import { resolveMediaUrl } from "../../../utils/mediaUrl";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

type EmployeePhotoEditorProps = {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  idPrefix: string;
};

type DragState = {
  startX: number;
  startY: number;
  startOffsetX: number;
  startOffsetY: number;
};

export const EmployeePhotoEditor: FC<EmployeePhotoEditorProps> = ({
  value,
  onChange,
  idPrefix,
}) => {
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [baseScale, setBaseScale] = useState(1);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [fileError, setFileError] = useState<string | null>(null);
  const [isTransformChanged, setIsTransformChanged] = useState(false);

  const imageRef = useRef<HTMLImageElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const editableImageCleanupRef = useRef<(() => void) | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const latestTransformRef = useRef({
    zoomPercent: 100,
    offsetX: 0,
    offsetY: 0,
  });
  const lastExportedValueRef = useRef<string | null>(null);
  const originalValueRef = useRef<string | null>(null);
  const isUserUploadRef = useRef(false);
  const initialTransformRef = useRef({
    zoomPercent: 100,
    offsetX: 0,
    offsetY: 0,
  });

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const revokeEditableImage = useCallback(() => {
    editableImageCleanupRef.current?.();
    editableImageCleanupRef.current = null;
  }, []);

  const exportCrop = useCallback(
    (img: HTMLImageElement, scale: number, ox: number, oy: number) => {
      const cropped = renderCroppedPhoto(img, {
        scale,
        offsetX: ox,
        offsetY: oy,
      });
      if (cropped) {
        lastExportedValueRef.current = cropped;
        onChange(cropped);
      }
    },
    [onChange],
  );

  const applyTransform = useCallback(
    (
      nextZoom: number,
      nextOffsetX: number,
      nextOffsetY: number,
      options: { shouldExport?: boolean; markAsChanged?: boolean } = {},
    ) => {
      const { shouldExport = true, markAsChanged = false } = options;
      const img = imageRef.current;
      if (!img || !naturalSize.width) return;
      const scale = baseScale * (nextZoom / 100);
      const clamped = clampOffsets(
        naturalSize.width,
        naturalSize.height,
        scale,
        nextOffsetX,
        nextOffsetY,
      );
      latestTransformRef.current = {
        zoomPercent: nextZoom,
        offsetX: clamped.offsetX,
        offsetY: clamped.offsetY,
      };
      setOffsetX(clamped.offsetX);
      setOffsetY(clamped.offsetY);
      if (markAsChanged) {
        setIsTransformChanged(true);
      }
      if (shouldExport) {
        exportCrop(img, clamped.scale, clamped.offsetX, clamped.offsetY);
      }
    },
    [baseScale, naturalSize, exportCrop],
  );

  const loadImageFromUrl = useCallback(
    async (url: string, isObjectUrl: boolean) => {
      setFileError(null);
      revokeEditableImage();
      try {
        const loaded = await loadEditableImage(url);
        if (!isObjectUrl) {
          editableImageCleanupRef.current = loaded.cleanup;
        }
        const img = loaded.image;
        const displayUrl = isObjectUrl ? url : resolveMediaUrl(url) ?? url;
        setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
        const cover = getInitialCoverScale(img.naturalWidth, img.naturalHeight);
        setBaseScale(cover);
        setZoomPercent(100);
        setOffsetX(0);
        setOffsetY(0);
        const initialTransform = {
          zoomPercent: 100,
          offsetX: 0,
          offsetY: 0,
        };
        latestTransformRef.current = initialTransform;
        initialTransformRef.current = initialTransform;
        setIsTransformChanged(false);
        isUserUploadRef.current = isObjectUrl;
        originalValueRef.current = isObjectUrl ? null : url;
        setSourceUrl(displayUrl);
        imageRef.current = img;
        if (isObjectUrl) {
          exportCrop(img, cover, 0, 0);
        } else {
          lastExportedValueRef.current = null;
        }
      } catch {
        setFileError("Не удалось загрузить изображение");
        if (isObjectUrl) URL.revokeObjectURL(url);
      }
    },
    [exportCrop, revokeEditableImage],
  );

  useEffect(() => {
    return () => {
      revokeObjectUrl();
      revokeEditableImage();
    };
  }, [revokeObjectUrl, revokeEditableImage]);

  useEffect(() => {
    if (!value) {
      if (objectUrlRef.current) {
        revokeObjectUrl();
      }
      revokeEditableImage();
      setSourceUrl(null);
      setNaturalSize({ width: 0, height: 0 });
      setZoomPercent(100);
      setOffsetX(0);
      setOffsetY(0);
      imageRef.current = null;
      originalValueRef.current = null;
      isUserUploadRef.current = false;
      setIsTransformChanged(false);
      return;
    }
    if (objectUrlRef.current) return;
    if (value === lastExportedValueRef.current) return;
    lastExportedValueRef.current = null;
    loadImageFromUrl(value, false);
  }, [value, loadImageFromUrl, revokeObjectUrl, revokeEditableImage]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    setFileError(null);
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError("Допустимые форматы: JPG, PNG, WebP");
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError("Размер файла не должен превышать 5 МБ");
      return;
    }

    revokeObjectUrl();
    revokeEditableImage();
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    loadImageFromUrl(url, true);
  };

  const handleRemove = () => {
    revokeObjectUrl();
    revokeEditableImage();
    setSourceUrl(null);
    setNaturalSize({ width: 0, height: 0 });
    setZoomPercent(100);
    setOffsetX(0);
    setOffsetY(0);
    latestTransformRef.current = {
      zoomPercent: 100,
      offsetX: 0,
      offsetY: 0,
    };
    lastExportedValueRef.current = null;
    originalValueRef.current = null;
    isUserUploadRef.current = false;
    setIsTransformChanged(false);
    imageRef.current = null;
    onChange(null);
  };

  const handleResetTransform = () => {
    const { zoomPercent: initialZoom, offsetX: initialX, offsetY: initialY } =
      initialTransformRef.current;
    setZoomPercent(initialZoom);
    setIsTransformChanged(false);

    const img = imageRef.current;
    if (!img || !naturalSize.width) return;

    const scale = baseScale * (initialZoom / 100);
    const clamped = clampOffsets(
      naturalSize.width,
      naturalSize.height,
      scale,
      initialX,
      initialY,
    );
    latestTransformRef.current = {
      zoomPercent: initialZoom,
      offsetX: clamped.offsetX,
      offsetY: clamped.offsetY,
    };
    setOffsetX(clamped.offsetX);
    setOffsetY(clamped.offsetY);

    if (!isUserUploadRef.current && originalValueRef.current) {
      lastExportedValueRef.current = originalValueRef.current;
      onChange(originalValueRef.current);
      return;
    }

    exportCrop(img, clamped.scale, clamped.offsetX, clamped.offsetY);
  };

  const handleZoomChange = (nextZoom: number) => {
    setZoomPercent(nextZoom);
    applyTransform(nextZoom, offsetX, offsetY, {
      markAsChanged: true,
    });
  };

  const startDrag = (clientX: number, clientY: number) => {
    if (!imageRef.current || !naturalSize.width) return;
    dragRef.current = {
      startX: clientX,
      startY: clientY,
      startOffsetX: offsetX,
      startOffsetY: offsetY,
    };
  };

  const moveDrag = (clientX: number, clientY: number) => {
    const drag = dragRef.current;
    if (!drag) return;
    const deltaX = clientX - drag.startX;
    const deltaY = clientY - drag.startY;
    applyTransform(
      zoomPercent,
      drag.startOffsetX + deltaX,
      drag.startOffsetY + deltaY,
      { shouldExport: false },
    );
  };

  const endDrag = () => {
    if (!dragRef.current) return;
    const drag = dragRef.current;
    dragRef.current = null;
    const { zoomPercent: zoom, offsetX: ox, offsetY: oy } =
      latestTransformRef.current;
    const hasMoved =
      Math.abs(ox - drag.startOffsetX) > 0.5 ||
      Math.abs(oy - drag.startOffsetY) > 0.5;
    applyTransform(zoom, ox, oy, {
      markAsChanged: hasMoved,
    });
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !imageRef.current || !naturalSize.width) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    startDrag(event.clientX, event.clientY);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    event.preventDefault();
    moveDrag(event.clientX, event.clientY);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    endDrag();
  };

  const handlePointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    endDrag();
  };

  const scale = baseScale * (zoomPercent / 100);
  const displayTransform =
    naturalSize.width > 0
      ? `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) scale(${scale})`
      : undefined;

  return (
    <div className={classes.root}>
      <p className={classes.title}>Фото сотрудника</p>
      <div className={classes.uploadRow}>
        {!sourceUrl && !value && (
          <input
            id={`${idPrefix}-photo-upload`}
            className={classes.fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
          />
        )}
        {(sourceUrl || value) && (
          <CustomButton
            content="Удалить фото"
            styleType="default"
            onClick={handleRemove}
          />
        )}
      </div>
      <p className={classes.hint}>
        Перетащите фото в рамке для позиционирования, используйте ползунок для
        масштаба
      </p>
      {fileError && <p className={classes.error}>{fileError}</p>}

      {sourceUrl && naturalSize.width > 0 && (
        <div className={classes.editor}>
          <div
            className={classes.viewport}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            role="presentation"
          >
            <img
              src={sourceUrl}
              alt=""
              className={classes.imageLayer}
              style={{
                width: naturalSize.width,
                height: naturalSize.height,
                transform: displayTransform,
              }}
              draggable={false}
            />
          </div>

          <div className={classes.controls}>
            <label className={classes.rangeField}>
              Масштаб
              <div className={classes.rangeRow}>
                <input
                  type="range"
                  min={MIN_ZOOM_PERCENT}
                  max={MAX_ZOOM_PERCENT}
                  step={1}
                  value={zoomPercent}
                  onChange={(e) => handleZoomChange(Number(e.target.value))}
                />
                <span>{zoomPercent}%</span>
              </div>
            </label>
            {isTransformChanged && (
              <CustomButton
                content="Сбросить"
                styleType="default"
                onClick={handleResetTransform}
              />
            )}
            {(value || sourceUrl) && (
              <div>
                <p className={classes.previewLabel}>Результат</p>
                <div className={classes.preview}>
                  <img
                    src={value ?? sourceUrl ?? ""}
                    alt="Предпросмотр фото сотрудника"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!sourceUrl && !value && (
        <div
          className={classes.viewport}
          style={{ cursor: "default", background: "#fafafa" }}
        />
      )}
    </div>
  );
};
